import React, { useCallback, useState } from 'react';
import { Col, ConfigProvider, Layout } from 'antd';
import { ConfigProvider as MobileConfigProvider } from 'antd-mobile';
import { Outlet, useLocation } from 'react-router-dom';
import UnAuthLayoutContext from '@/context/UnAuthLayoutContext';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import Sidebar from './Sidebar';
import {
  CloseOutlined,
  DollarCircleOutlined,
  GiftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import styles from './UnAuthenticatedLayout.module.pcss';
import { organizationConfig, whiteLabelingOrganisations } from '@/context/AdminLayoutContext';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import { MenuItem } from '@/types';
import enUS from 'antd/locale/en_US';
import { If } from '@/components';
import enUSMobile from 'antd-mobile/es/locales/en-US';

const { Content } = Layout;

export default function UnAuthenticatedLayout() {
  const currentUser = useReactiveVar(currentUserVar);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const orgName = currentUser.organization.name as 'ARYA';
  const isMobile = useMediaQuery({ query: '(max-width: 900px)' });
  const [isCollapsed, setCollapsed] = useState(isMobile);

  const menuItems: MenuItem[] = [
    {
      title: 'My Earnings',
      path: `/my-earnings`,
      params: location.search,
      icon: <DollarCircleOutlined />,
    },
    {
      title: 'My Rewards',
      path: `/my-rewards`,
      params: location.search,
      icon: <GiftOutlined />,
    },
    {
      title: 'My Transactions',
      path: `/my-transactions`,
      params: location.search,
      icon: <ReconciliationOutlined />,
    },
  ];

  const getContext = () => {
    return {
      isMobile,
      layoutConfig: !whiteLabelingOrganisations.includes(currentUser.organization.name)
        ? organizationConfig['ARYA']
        : organizationConfig[orgName],
      siderWidth: isCollapsed ? 80 : 200,
    };
  };

  const filteredMenus = menuItems.filter((item) => {
    return token ? item.path === '/my-earnings' : item.path !== '/my-earnings';
  });

  const toggleSideBar = useCallback(() => {
    setCollapsed((prevState) => !prevState);
  }, []);

  const defineToggleIcon = () => {
    if (isCollapsed) {
      return MenuUnfoldOutlined;
    } else {
      if (isMobile) {
        return CloseOutlined;
      }
      return MenuFoldOutlined;
    }
  };

  const toggleIcon = React.createElement(defineToggleIcon(), {
    className: classNames(styles.collapseTrigger, styles[getContext().layoutConfig.title.name]),
    onClick: toggleSideBar,
  });

  const layout = (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar menuItems={filteredMenus} isCollapsed={isCollapsed} />
      <Layout className={styles.contentLayout}>
        <Layout.Header className={classNames(styles.header, styles[getContext().layoutConfig.title.name])}>
          <If condition={!isMobile}>
            <Col style={!isCollapsed && isMobile ? { padding: '0 180px' } : undefined}>{toggleIcon}</Col>
          </If>
        </Layout.Header>
        <Content className={styles.content}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );

  return (
    <UnAuthLayoutContext.Provider value={getContext()}>
      <If condition={isMobile}>
        <MobileConfigProvider locale={enUSMobile}>{layout}</MobileConfigProvider>
      </If>
      <If condition={!isMobile}>
        <ConfigProvider
          locale={enUS}
          theme={{
            token: {
              fontFamily: 'Manrope-Medium',
              colorPrimary: '#202020',
            },
          }}
        >
          {layout}
        </ConfigProvider>
      </If>
    </UnAuthLayoutContext.Provider>
  );
}
